import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, forwardRef, ViewChild, Output, Input, ViewEncapsulation, Component } from '@angular/core';
import * as i2 from '@angular/forms';
import { UntypedFormGroup, Validators, ReactiveFormsModule, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { E11IconModule } from '@engineering11/ui-lib/e11-icon';
import * as i5 from '@engineering11/ui-lib/e11-inline-footer';
import { E11InlineFooterModule } from '@engineering11/ui-lib/e11-inline-footer';
import * as i4 from '@engineering11/ui-lib/e11-inline-label';
import { E11InlineLabelModule } from '@engineering11/ui-lib/e11-inline-label';
import { E11InputLabelModule } from '@engineering11/ui-lib/e11-input-label';
import * as i3 from '@engineering11/ui-lib/e11-select';
import { E11SelectModule } from '@engineering11/ui-lib/e11-select';

const autocompleteSizeMap = {
    sm: 'text-sm',
    md: 'text-md',
    lg: 'text-lg',
    xl: 'text-xl',
};
let nextUniqueId = 0;
class E11InlineSelectComponent {
    set value(value) {
        this._value = value;
        this.displayValue = this.buildDisplayValue(value);
        this.persistValue();
    }
    constructor(cd) {
        this.cd = cd;
        this.options = [];
        this.id = `e11-select-${nextUniqueId++}`;
        this.loading = false;
        this.editing = false;
        this.required = false;
        this.editIcon = false;
        this.hasError = false;
        this.size = 'md';
        this.formControlName = '';
        this.parentForm = new UntypedFormGroup({});
        this.inputSaved = new EventEmitter();
        this.inputChange = new EventEmitter();
        this.inputValue = '';
        this.onChange = () => { };
        this.onTouch = () => { };
        this.invalidOption = false;
        this.disabledClick = false;
        this.optionSelected = false;
        this.labelClicked = false;
        this.autoCompleteSizeMap = autocompleteSizeMap;
        this.Validators = Validators;
    }
    ngAfterViewInit() {
        this.displayValue = this.buildDisplayValue(this.f[this.formControlName].value);
        this.cd.detectChanges();
    }
    ngOnChanges(changes) {
        this.displayValue = this.buildDisplayValue(this.f[this.formControlName].value);
    }
    ngOnInit() {
        this.initFormMethods();
    }
    get f() {
        return this.parentForm.controls;
    }
    initFormMethods() {
        if (!this.formControlName || !this.parentForm)
            return;
        this.formControl = this.f[this.formControlName];
        if (!this.formControl)
            return;
        if (this.required && !this.formControl.hasValidator(Validators.required))
            this.formControl.addValidators([Validators.required]);
        if (this._value) {
            this.displayValue = this.buildDisplayValue(this._value);
            this.persistValue();
            this.updateFormValue(this._value);
        }
        this.formControl.updateValueAndValidity();
    }
    handleClickOutside() {
        if (this.optionSelected) {
            this.optionSelected = false;
            return;
        }
        if (this.labelClicked) {
            this.labelClicked = false;
            return;
        }
        this.cancelEdit();
    }
    inputSelected(value) {
        this.hasError = false;
        if (this.required && (value == '' || value === null || value == undefined)) {
            this.hasError = true;
        }
        this.optionSelected = true;
        this.updateFormValue(value);
        this.displayValue = this.buildDisplayValue(value);
    }
    saveEdit() {
        this._value = this.savedValue = this.f[this.formControlName].value;
        if (this._value === '')
            this._value = undefined;
        this.updateFormValue(this._value);
        this.displayValue = this.buildDisplayValue(this._value);
        this.inputSaved.emit(this._value);
        this.toggleEditing();
    }
    cancelEdit() {
        this.updateFormValue(this.savedValue);
        this.toggleEditing();
    }
    toggleEditing(labelClicked = false) {
        if (labelClicked)
            this.labelClicked = true;
        this.editing = !this.editing;
        this.cd.detectChanges();
        this.f[this.formControlName].markAsPristine();
    }
    buildDisplayValue(value) {
        if (!value)
            return undefined;
        return this.options.find((data) => data.value === value)?.name;
    }
    checkValidOption() {
        this.inputSelect.validate(this.f[this.formControlName]);
    }
    writeValue(obj) {
        this.inputValue = obj ? obj : '';
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    validate(control) {
        if (control.hasValidator(Validators.required) && (control.value == '' || control.value === null || control.value === 'null')) {
            this.hasError = this.hasError ? true : control.touched;
            return {
                required: true,
            };
        }
        this.hasError = false;
        return null;
    }
    handleOnChange(event) {
        let updatedValue = event.target.value.trim();
        this.updateFormValue(updatedValue);
    }
    updateFormValue(formValue) {
        if (this.formControlName && this.f[this.formControlName]) {
            this.f[this.formControlName].setValue(formValue);
        }
        this.f[this.formControlName].updateValueAndValidity();
    }
    persistValue() {
        if (this.formControlName && !this.f[this.formControlName]?.dirty) {
            this.savedValue = this.f[this.formControlName].value;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineSelectComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11InlineSelectComponent, isStandalone: true, selector: "e11-inline-select", inputs: { label: "label", value: "value", options: "options", placeholder: "placeholder", id: "id", loading: "loading", editing: "editing", required: "required", editIcon: "editIcon", hasError: "hasError", size: "size", tooltipConfig: "tooltipConfig", requiredTooltipConfig: "requiredTooltipConfig", formControlName: "formControlName", parentForm: "parentForm" }, outputs: { inputSaved: "inputSaved", inputChange: "inputChange" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11InlineSelectComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "inputSelect", first: true, predicate: ["input"], descendants: true }], usesOnChanges: true, ngImport: i0, template: `
    <div [formGroup]="parentForm">
      <ng-container *ngIf="!editing">
        <e11-inline-label
          [label]="label"
          [value]="displayValue"
          [placeholder]="placeholder"
          [loading]="loading"
          [size]="size"
          [hasError]="required ? f[formControlName].errors?.required : undefined"
          [required]="required"
          [editIcon]="true"
          [tooltipConfig]="tooltipConfig"
          [requiredTooltipConfig]="requiredTooltipConfig"
          (labelClicked)="toggleEditing(true)"
        ></e11-inline-label>
      </ng-container>
      <div (closeOnClickOutside)="handleClickOutside()" *ngIf="editing" class="e11-w-full">
        <div class="e11-mb-1">
          <div class="{{ autoCompleteSizeMap[size] }}" [ngClass]="{ 'input-error': hasError }">
            <e11-select
              #input
              [required]="required"
              [id]="id"
              [options]="options"
              [loading]="loading"
              [formControlName]="formControlName"
              [parentForm]="parentForm"
              [hasError]="hasError"
              (valueChange)="inputSelected($event)"
              (keydown.enter)="saveEdit()"
              (inputChanged)="inputChange.emit($event)"
            ></e11-select>
          </div>
        </div>
        <div class="e11-flex e11-justify-end">
          <div class="e11-absolute">
            <e11-inline-footer [confirmDisabled]="hasError" (confirm)="saveEdit()" (cancel)="cancelEdit()"></e11-inline-footer>
          </div>
        </div>
      </div>
    </div>
  `, isInline: true, dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "ngmodule", type: FormsModule }, { kind: "ngmodule", type: E11IconModule }, { kind: "ngmodule", type: E11InputLabelModule }, { kind: "ngmodule", type: E11SelectModule }, { kind: "component", type: i3.E11SelectComponent, selector: "e11-select", inputs: ["id", "name", "label", "size", "selectMessage", "labelClass", "inputClass", "containerClass", "parentForm", "formControlName", "options", "value", "hasError", "required", "disabled", "loading", "tooltipConfig", "labelTextOptional", "selectClass"], outputs: ["valueChange", "onValueChange"] }, { kind: "ngmodule", type: E11InlineLabelModule }, { kind: "component", type: i4.E11InlineLabelComponent, selector: "e11-inline-label", inputs: ["label", "value", "placeholder", "size", "loading", "hasError", "required", "editIcon", "tooltipConfig", "requiredTooltipConfig"], outputs: ["labelClicked"] }, { kind: "ngmodule", type: E11InlineFooterModule }, { kind: "component", type: i5.E11InlineFooterComponent, selector: "e11-inline-footer", inputs: ["confirmIcon", "cancelIcon", "confirmDisabled"], outputs: ["confirm", "cancel"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineSelectComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'e11-inline-select',
                    standalone: true,
                    imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        FormsModule,
                        E11IconModule,
                        E11InputLabelModule,
                        E11SelectModule,
                        E11InlineLabelModule,
                        E11InlineFooterModule,
                    ],
                    template: `
    <div [formGroup]="parentForm">
      <ng-container *ngIf="!editing">
        <e11-inline-label
          [label]="label"
          [value]="displayValue"
          [placeholder]="placeholder"
          [loading]="loading"
          [size]="size"
          [hasError]="required ? f[formControlName].errors?.required : undefined"
          [required]="required"
          [editIcon]="true"
          [tooltipConfig]="tooltipConfig"
          [requiredTooltipConfig]="requiredTooltipConfig"
          (labelClicked)="toggleEditing(true)"
        ></e11-inline-label>
      </ng-container>
      <div (closeOnClickOutside)="handleClickOutside()" *ngIf="editing" class="e11-w-full">
        <div class="e11-mb-1">
          <div class="{{ autoCompleteSizeMap[size] }}" [ngClass]="{ 'input-error': hasError }">
            <e11-select
              #input
              [required]="required"
              [id]="id"
              [options]="options"
              [loading]="loading"
              [formControlName]="formControlName"
              [parentForm]="parentForm"
              [hasError]="hasError"
              (valueChange)="inputSelected($event)"
              (keydown.enter)="saveEdit()"
              (inputChanged)="inputChange.emit($event)"
            ></e11-select>
          </div>
        </div>
        <div class="e11-flex e11-justify-end">
          <div class="e11-absolute">
            <e11-inline-footer [confirmDisabled]="hasError" (confirm)="saveEdit()" (cancel)="cancelEdit()"></e11-inline-footer>
          </div>
        </div>
      </div>
    </div>
  `,
                    providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11InlineSelectComponent),
                            multi: true,
                        },
                    ],
                    encapsulation: ViewEncapsulation.None,
                }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { label: [{
                type: Input
            }], value: [{
                type: Input
            }], options: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], id: [{
                type: Input
            }], loading: [{
                type: Input
            }], editing: [{
                type: Input
            }], required: [{
                type: Input
            }], editIcon: [{
                type: Input
            }], hasError: [{
                type: Input
            }], size: [{
                type: Input
            }], tooltipConfig: [{
                type: Input
            }], requiredTooltipConfig: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], inputSaved: [{
                type: Output
            }], inputChange: [{
                type: Output
            }], inputSelect: [{
                type: ViewChild,
                args: ['input', { static: false }]
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { E11InlineSelectComponent };
